import type { ReactNode } from 'react';
import React, { useState, useRef } from 'react';
import Button from '../../atoms/button/Button';
import Dialog from '../../molecules/dialog/Dialog';
import Text from '../../atoms/text/Text';
import Input from '../../atoms/input/Input';
import './WordBookButton.css';

const LOCALSTORAGE_KEY = 'cinny_wordbook_list';

// 型別 workaround for JS Button in TSX
const TypedButton = Button as React.FC<{
  children: ReactNode;
  variant?: string;
  iconSrc?: string;
  type?: string;
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
  id?: string;
}>;

function loadWordbook(): string[] {
  try {
    const data = localStorage.getItem(LOCALSTORAGE_KEY);
    if (!data) return [];
    return JSON.parse(data);
  } catch {
    return [];
  }
}

function saveWordbook(list: string[]) {
  localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(list));
}

export interface WordBookButtonProps {
  onSelect: (word: string) => void;
}

export const WordBookButton: React.FC<WordBookButtonProps> = ({ onSelect }) => {
  const [open, setOpen] = useState(false);
  const [wordList, setWordList] = useState<string[]>(() => loadWordbook());
  const [newWord, setNewWord] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [format, setFormat] = useState<'json' | 'csv'>('csv');
  const jsonFileInputRef = useRef<HTMLInputElement>(null);
  const csvFileInputRef = useRef<HTMLInputElement>(null);

  const handleAdd = () => {
    if (newWord.trim().length === 0) return;
    if (newWord.length > 256) {
      setError('辭彙最長256字元');
      return;
    }
    if (wordList.includes(newWord)) {
      setError('辭彙已存在');
      return;
    }
    const updated = [newWord, ...wordList];
    setWordList(updated);
    saveWordbook(updated);
    setNewWord('');
    setError(null);
  };

  const handleSelect = (word: string) => {
    onSelect(word);
    setOpen(false);
  };

  const handleExportJSON = () => {
    const blob = new Blob([JSON.stringify(wordList, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'wordbook.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImportJSON = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const arr = JSON.parse(ev.target?.result as string);
        if (!Array.isArray(arr) || !arr.every(w => typeof w === 'string')) throw new Error();
        setWordList(arr);
        saveWordbook(arr);
        setError(null);
      } catch {
        setError('匯入格式錯誤');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleExportCSV = () => {
    const header = 'word\n';
    const csvContent = wordList.map(word => `"${word.replace(/"/g, '""')}"`).join('\n');
    const blob = new Blob([header + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'wordbook.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImportCSV = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const text = ev.target?.result as string;
        const lines = text.split(/\r?\n/);
        // Skip header if it exists
        if (lines[0].trim().toLowerCase() === 'word') {
          lines.shift();
        }
        const newWords = lines
          .map(line => {
            line = line.trim();
            if (line.startsWith('"') && line.endsWith('"')) {
              line = line.substring(1, line.length - 1);
            }
            return line.replace(/""/g, '"');
          })
          .filter(word => word.length > 0);

        const uniqueNewWords = [...new Set(newWords)];

        setWordList(uniqueNewWords);
        saveWordbook(uniqueNewWords);
        setError(null);
      } catch {
        setError('匯入格式錯誤');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleExportClick = () => {
    if (format === 'json') {
      handleExportJSON();
    } else {
      handleExportCSV();
    }
  };

  const handleImportClick = () => {
    if (format === 'json') {
      jsonFileInputRef.current?.click();
    } else {
      csvFileInputRef.current?.click();
    }
  };

  return (
    <>
      <TypedButton variant="primary" className="wordbook-entry-btn" onClick={() => setOpen(true)}>辭庫</TypedButton>
      {open && (
        <Dialog isOpen={open} title={
          <div className="wordbook-dialog-title">
            <Text variant="h2" weight="medium" primary>辭庫</Text>
          </div>
        } onRequestClose={() => setOpen(false)}>
          <div style={{ minWidth: 420, display: 'flex', flexDirection: 'column', gap: 16, padding: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', gap: 16 }}>
                <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <input
                    type="radio"
                    name="format"
                    value="csv"
                    checked={format === 'csv'}
                    onChange={() => setFormat('csv')}
                    style={{ appearance: 'auto', opacity: 1, position: 'static', width: '16px', height: '16px' }}
                  />
                  CSV
                </label>
                <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <input
                    type="radio"
                    name="format"
                    value="json"
                    checked={format === 'json'}
                    onChange={() => setFormat('json')}
                    style={{ appearance: 'auto', opacity: 1, position: 'static', width: '16px', height: '16px' }}
                  />
                  JSON
                </label>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <TypedButton onClick={handleExportClick} variant="primary" className="wordbook-green-btn">匯出</TypedButton>
                <TypedButton onClick={handleImportClick} variant="primary" className="wordbook-green-btn">匯入</TypedButton>
                <input ref={jsonFileInputRef} type="file" accept="application/json" style={{ display: 'none' }} onChange={handleImportJSON} />
                <input ref={csvFileInputRef} type="file" accept=".csv, text/csv" style={{ display: 'none' }} onChange={handleImportCSV} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, width: '100%' }}>
              <div style={{ flex: 1 }}>
                <Input
                  value={newWord}
                  onChange={e => { setNewWord(e.target.value); setError(null); }}
                  placeholder="新增辭彙"
                />
              </div>
              <div style={{ flex: 0 }}>
                <TypedButton onClick={handleAdd} variant="primary">新增</TypedButton>
              </div>
            </div>
            {error && <Text variant="b2" style={{ color: 'var(--curve-danger-text, #EF4444)' }}>{error}</Text>}
            <div style={{
              maxHeight: 360,
              minHeight: 40,
              overflowY: wordList.length > 10 ? 'auto' : 'visible',
              border: '1px solid var(--curve-border, #E5E5E5)',
              borderRadius: 8,
              padding: '4px',
              display: 'flex',
              flexDirection: 'column',
              gap: 0,
            }}>
              {wordList.length === 0 && <Text variant="b2" style={{ color: 'var(--curve-text-muted, #64748B)', padding: 8 }}>尚無辭彙</Text>}
              {wordList.map((w, idx) => (
                <div key={w} style={{ display: 'flex', alignItems: 'center' }}>
                  <button
                    type="button"
                    className="wordbook-list-btn"
                    style={{
                      cursor: 'pointer',
                      background: idx % 2 === 0 ? 'var(--curve-bg-white, #fff)' : 'var(--curve-bg-gray, #f6f6f6)',
                      border: 'none',
                      borderBottom: idx === wordList.length - 1 ? 'none' : '1px solid var(--curve-border, #e0e0e0)',
                      textAlign: 'left',
                      padding: '8px 12px',
                      width: '100%',
                      fontSize: '1rem',
                    }}
                    onClick={() => handleSelect(w)}
                  >
                    {w}
                  </button>
                  <button
                    type="button"
                    aria-label="刪除"
                    style={{
                      marginLeft: 4,
                      background: 'none',
                      border: 'none',
                      color: 'var(--curve-danger-text, #EF4444)',
                      fontSize: '1.2rem',
                      cursor: 'pointer',
                      padding: '0 8px',
                      height: 32,
                    }}
                    onClick={() => {
                      const updated = wordList.filter(word => word !== w);
                      setWordList(updated);
                      saveWordbook(updated);
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 8 }}>
              <div style={{ minWidth: 96 }}>
                <TypedButton onClick={() => setOpen(false)} variant="primary">關閉</TypedButton>
              </div>
            </div>
          </div>
        </Dialog>
      )}
    </>
  );
};
